import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, MoreHorizontal, Newspaper, Plus, Rocket, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  useDeletePost,
  useGoLive,
  useMySites,
  usePosts,
  useSubmitPost,
  useUnpublish,
} from '@/lib/blog';
import { isAdminSurface } from '@/lib/surface';
import { formatDate } from '@/lib/format';
import type { BlogPostData } from '@/types/api';

type StatusFilter = '' | 'draft' | 'submitted';

export function BlogIndex() {
  const [params, setParams] = useSearchParams();
  const sites = useMySites();
  const siteList = useMemo(() => sites.data?.data ?? [], [sites.data]);

  const siteId = useMemo(() => {
    const fromUrl = Number(params.get('site'));
    if (fromUrl && siteList.some((s) => s.id === fromUrl)) return fromUrl;
    return siteList[0]?.id;
  }, [params, siteList]);

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);

  const posts = usePosts(siteId, {
    q: q || undefined,
    status: status || undefined,
    page,
  });
  const data = posts.data?.data;
  const meta = posts.data?.meta;

  const navigate = useNavigate();
  const site = siteList.find((s) => s.id === siteId);

  function selectSite(id: number) {
    const next = new URLSearchParams(params);
    next.set('site', String(id));
    setParams(next);
    setPage(1);
  }

  const newHref = siteId ? `/blog/new?site=${siteId}` : '/blog/new';

  if (sites.isLoading) {
    return <SkeletonList rows={5} />;
  }

  if (siteList.length === 0) {
    return (
      <EmptyState
        icon={Newspaper}
        title="No blog yet"
        description={
          isAdminSurface
            ? 'Create a site for a client first, then their posts will show up here.'
            : "Your blog isn't set up yet. Reach out to the Mehrutherm team to get started."
        }
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Newspaper}
        title="Blog"
        subtitle={
          isAdminSurface
            ? 'Review submitted posts and promote them to the live channel.'
            : 'Write and manage your posts. Submit for publishing when a draft is ready.'
        }
        action={
          <Button onClick={() => navigate(newHref)}>
            <Plus className="size-4" /> New post
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        {siteList.length > 1 ? (
          <Select
            value={siteId ?? ''}
            onChange={(e) => selectSite(Number(e.target.value))}
            className="max-w-[220px]"
          >
            {siteList.map((s) => (
              <option key={s.id} value={s.id}>
                {isAdminSurface && s.client_name ? `${s.client_name} — ${s.name}` : s.name}
              </option>
            ))}
          </Select>
        ) : null}
        <Input
          placeholder="Search posts…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as StatusFilter);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
        </Select>
      </div>

      {posts.isLoading ? (
        <SkeletonList rows={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Newspaper}
          title="No posts yet"
          description={
            isAdminSurface
              ? 'Nothing to review here yet.'
              : `Start writing ${site ? `for ${site.name}` : ''}. Your first draft is a click away.`
          }
          action={
            <Button onClick={() => navigate(newHref)}>
              <Plus className="size-4" /> New post
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {data.map((post) => (
              <PostRow
                key={post.id}
                post={post}
                siteId={siteId!}
                onOpen={() => navigate(`/blog/${post.id}?site=${siteId}`)}
              />
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function PostRow({
  post,
  siteId,
  onOpen,
}: {
  post: BlogPostData;
  siteId: number;
  onOpen: () => void;
}) {
  const submit = useSubmitPost(siteId);
  const goLive = useGoLive(siteId);
  const unpublish = useUnpublish(siteId);
  const del = useDeletePost(siteId);
  const confirm = useConfirm();
  const toast = useToast();

  const timestamp = post.is_live
    ? `Live · ${formatDate(post.live_at ?? post.updated_at)}`
    : post.submitted_at
      ? `Submitted · ${formatDate(post.submitted_at)}`
      : `Updated · ${formatDate(post.updated_at)}`;

  return (
    <DataListRow onClick={onOpen} interactive>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{post.title || 'Untitled'}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">/{post.slug}</span>
          <span aria-hidden>·</span>
          <span>{timestamp}</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {post.status === 'submitted' ? <StatusPill tone="info">Submitted</StatusPill> : null}
        {post.is_live ? (
          <StatusPill tone="success">Live</StatusPill>
        ) : post.status === 'draft' ? (
          <StatusPill tone="neutral">Draft</StatusPill>
        ) : null}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger
          onClick={(e) => e.stopPropagation()}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100"
          aria-label={`Actions for ${post.title}`}
        >
          <MoreHorizontal className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={onOpen}>Open</DropdownMenuItem>
          {!isAdminSurface && post.status === 'draft' ? (
            <DropdownMenuItem
              onClick={async () => {
                await submit.mutateAsync(post.id);
                toast.success('Submitted for publishing', 'The Mehrutherm team has been notified.');
              }}
            >
              <CheckCircle2 className="size-4" /> Submit for publishing
            </DropdownMenuItem>
          ) : null}
          {isAdminSurface && post.status === 'submitted' ? (
            <DropdownMenuItem
              onClick={async () => {
                await goLive.mutateAsync(post.id);
                toast.success(`"${post.title}" is live`);
              }}
            >
              <Rocket className="size-4" /> Go live
            </DropdownMenuItem>
          ) : null}
          {isAdminSurface && post.is_live ? (
            <DropdownMenuItem
              onClick={async () => {
                const ok = await confirm({
                  title: `Unpublish "${post.title}"?`,
                  body: 'It will be removed from the live channel. The post itself is kept.',
                  confirmLabel: 'Unpublish',
                  tone: 'danger',
                });
                if (ok) {
                  await unpublish.mutateAsync(post.id);
                  toast.success('Removed from the live channel');
                }
              }}
            >
              <Undo2 className="size-4" /> Unpublish
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            onClick={async () => {
              const ok = await confirm({
                title: `Delete "${post.title || 'Untitled'}"?`,
                body: post.is_live
                  ? 'It comes off the live site as well as out of this list. Unpublish instead if you only want it off the site.'
                  : 'The draft and its images are removed from this list.',
                confirmLabel: 'Delete post',
                tone: 'danger',
              });
              if (ok) {
                await del.mutateAsync(post.id);
                toast.success('Post deleted');
              }
            }}
          >
            <Trash2 className="size-4" /> Delete post
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </DataListRow>
  );
}
